import type React from 'react';
import i18n from '../../../i18n';
import type { DepartmentUserRole } from '../../../lib/departments';

/** Fehlermeldung aus einer {data, meta, errors}-Antwort (422 u. a.) oder Fallback. */
export function apiErrorMessage(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { errors?: unknown; message?: unknown } } })?.response?.data;
  const errors = data?.errors;
  // Bekannte Fehlercodes der API → verständliche Meldung.
  const first = Array.isArray(errors) ? (errors[0] as { code?: string } | undefined) : undefined;
  const coded = first?.code ? CODE_KEYS[first.code] : undefined;
  if (coded) return i18n.t(coded);
  const pick = (v: unknown): string | null => {
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v)) { for (const x of v) { const r = pick(x); if (r) return r; } return null; }
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.message === 'string' && o.message) return o.message;
      for (const x of Object.values(o)) { const r = pick(x); if (r) return r; }
    }
    return null;
  };
  const raw = pick(errors) ?? (typeof data?.message === 'string' && data.message ? data.message : null);
  // Keine Datenbank-Spaltennamen in der Oberfläche (z. B. „user_role"): solche Meldungen durch den Fallback ersetzen.
  if (!raw || /\b[a-z]+_[a-z_]+\b/.test(raw)) return fallback;
  return raw;
}

const CODE_KEYS: Record<string, string> = {
  E_PERM_ESCALATION: 'departments.errors.perm_escalation',
  E_STAGE_IN_USE: 'departments.errors.stage_in_use',
  E_DEPARTMENT_PROTECTED: 'departments.errors.protected',
  E_STAGES_READONLY: 'departments.errors.stages_readonly',
};

export const SLUG_RE = /^[a-z0-9-]{2,50}$/;

/** Kürzel-Vorschlag aus dem Namen: Kleinbuchstaben, Umlaute aufgelöst, Bindestriche. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

export function groupKey(userRole: DepartmentUserRole): 'pb' | 'bk' {
  return userRole === 2 ? 'pb' : 'bk';
}

export function selectStyle(t: { border: string; heading: string; inputBg: string }): React.CSSProperties {
  return {
    width: '100%', border: `1px solid ${t.border}`, borderRadius: 9,
    padding: '9px 12px', fontSize: 13, color: t.heading, background: t.inputBg,
    fontFamily: 'Outfit, sans-serif', cursor: 'pointer', outline: 'none',
  };
}
